/*
 * Mivoza – bundelbox "Mehr kaufen, mehr sparen" + prijsrij + actiebalk met countdown (HHSaleBar, ongewijzigd uit Haus-Her).
 * Overgenomen uit Haus-Her (assets/hh.js, 22 okt 2026): de klasse HHQtyBreaks is
 * ongewijzigd, op twee punten na:
 *  1. getBundleItems() levert ook voor producten zonder varianten zelf de regel
 *     (Mivoza's koopknoppen hebben geen zichtbaar aantal-veld);
 *  2. verversen na een variantwissel gaat via Mivoza's Horizon-event
 *     'shopify:product:select' (event.promise -> detail.html), net als
 *     assets/mivoza-variant-sync.js, in plaats van Haus-Her's 'variant:update'.
 * product-form.js vraagt getBundleItems() op bij elke klik op de koopknop.
 */
(() => {
  const selectedTier = {};

  /* ---------- Mengenrabatt + Variantenwahl pro Stück ---------- */
  // Formatiert Cent-Beträge wie der Liquid-money-Filter der aktuellen Marktwährung.
  // sample = Ausgabe von {{ 123456 | money }}, z. B. "€1.234,56" oder "CHF 1,234.56".
  const formatMoney = (cents, sample) => {
    const m = /^(.*?)1(\D?)234(?:(\D)56)?(.*)$/.exec(sample || '');
    if (!m) return (cents / 100).toFixed(2);
    const [, prefix, thousands, decimal, suffix] = m;
    const [int, frac] = (cents / 100).toFixed(decimal ? 2 : 0).split('.');
    return prefix + int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (decimal ? decimal + frac : '') + suffix;
  };

  class HHQtyBreaks extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.shopify-section') || document;
      this.section.classList?.add('hh-has-qb');
      const dataEl = this.querySelector('[data-hh-qb-data]');
      this.data = dataEl ? JSON.parse(dataEl.textContent) : null;
      if (this.data) this.section.classList?.add('hh-qb-owns-variants');
      const remembered = selectedTier[this.dataset.hhRefresh];
      if (remembered) {
        const input = this.querySelector(`.hh-qb__head input[value="${remembered}"]`);
        if (input) input.checked = true;
      }
      this.addEventListener('change', this.onChange);
      this.sync();
      this.updatePrices();
    }
    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
    }
    get tier() {
      return Number(this.querySelector('.hh-qb__head input:checked')?.value || 1);
    }
    onChange = (event) => {
      const target = event.target;
      if (target instanceof HTMLSelectElement && target.dataset.slot) {
        // Gleiche Stück-Nr. in allen Stufen synchron halten, damit ein Stufenwechsel die Wahl behält
        this.querySelectorAll(`select[data-slot="${target.dataset.slot}"][data-opt="${target.dataset.opt}"]`).forEach(
          (sel) => { if (sel !== target) sel.value = target.value; }
        );
        if (target.dataset.slot === '1') this.syncMainPicker(Number(target.dataset.opt), target.value);
        this.updatePrices();
        return;
      }
      const checked = this.querySelector('.hh-qb__head input:checked');
      if (checked) selectedTier[this.dataset.hhRefresh] = checked.value;
      this.sync();
    };
    /** Gewählte Variante für Stück n (1-basiert) */
    variantForSlot(n) {
      if (!this.data) return null;
      const values = Array.from(this.querySelectorAll(`select[data-slot="${n}"]`))
        .reduce((acc, sel) => { acc[Number(sel.dataset.opt)] = sel.value; return acc; }, []);
      return this.data.variants.find((v) => v.options.every((o, i) => o === values[i])) || null;
    }
    /** Versteckte Horizon-Variantenauswahl nachziehen (Bilder, Preis, Formular-ID, URL) */
    syncMainPicker(optIndex, value) {
      const picker = this.section.querySelector('variant-picker');
      if (!picker) return;
      const fieldset = picker.querySelector(`fieldset[data-fieldset-index="${optIndex}"]`);
      const radio = Array.from(fieldset?.querySelectorAll('input[type="radio"]') || []).find((r) => r.value === value);
      if (radio && !radio.checked) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        return;
      }
      const select = picker.querySelectorAll('select')[optIndex];
      if (select && select.value !== value) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    updatePrices() {
      if (!this.data) return;
      const money = (c) => formatMoney(c, this.dataset.moneySample);
      this.querySelectorAll('.hh-qb__opt').forEach((opt) => {
        const tier = Number(opt.dataset.tier);
        const pct = Number(opt.dataset.pct) || 0;
        const counts = new Map();
        let was = 0;
        let missing = false;
        for (let n = 1; n <= tier; n++) {
          const v = this.variantForSlot(n);
          if (!v) { missing = true; break; }
          counts.set(v, (counts.get(v) || 0) + 1);
          was += Math.max(v.compare || 0, v.price);
        }
        if (missing) return;
        // Shopify rundet den Kollektions-Rabatt je STÜCK ab (verifiziert: 2 × 59,95 € −10 % = 107,92 €)
        let sum = 0;
        let total = 0;
        for (const [v, qty] of counts) {
          sum += v.price * qty;
          total += (v.price - Math.floor((v.price * pct) / 100)) * qty;
        }
        opt.querySelector('[data-qb-total]').textContent = money(total);
        const wasEl = opt.querySelector('[data-qb-was]');
        if (wasEl) { wasEl.textContent = money(was); wasEl.hidden = !(was > total); }
        // "50% Rabatt!" / "50% + 10% extra Rabatt!" – Sale-% aus Streichpreis vs. Preis der gewählten Stücke
        const salePct = was > sum ? Math.round(((was - sum) * 100) / was) : 0;
        const sub = opt.querySelector('[data-qb-sub]');
        if (sub) {
          const d = this.dataset;
          const tpl = pct > 0 ? (salePct > 0 ? d.tplSaleExtra : d.tplExtra) : salePct > 0 ? d.tplSale : d.tplPlain;
          sub.textContent = (tpl || '').replace('[sale]', salePct).replace('[extra]', pct);
          sub.classList.toggle('hh-qb__sub--deal', pct > 0 || salePct > 0);
        }
      });
      this.showError('');
      this.updateStock();
    }
    /** Lagerhinweis für Stück #1 – nur mit echtem Shopify-Bestand */
    updateStock() {
      const el = this.querySelector('[data-qb-stock]');
      const v = this.variantForSlot(1);
      if (!el || !v) return;
      const show = typeof v.stock === 'number' && v.stock > 0 && v.stock <= Number(el.dataset.threshold);
      el.hidden = !show;
      if (show) el.querySelector('[data-qb-stock-text]').textContent = el.dataset.tpl.replace('[anzahl]', v.stock);
    }
    showError(msg) {
      const el = this.querySelector('[data-qb-error]');
      if (!el) return;
      el.textContent = msg;
      el.hidden = !msg;
    }
    /**
     * Für product-form.js: Positionen für den Warenkorb.
     * null = normaler Ablauf (Produkt ohne Varianten), false = Abbruch (Fehler angezeigt).
     * @returns {Array<{variantId: string, quantity: number}> | null | false}
     */
    getBundleItems() {
      if (!this.data) {
        // Mivoza: geen zichtbaar aantal-veld in de koopknoppen -> zelf de juiste regel aanleveren
        const idInput = this.section.querySelector('product-form-component input[name="id"], form[data-type="add-to-cart-form"] input[name="id"]');
        if (!idInput || !idInput.value) return null;
        return [{ variantId: String(idInput.value), quantity: this.tier }];
      }
      const counts = new Map();
      for (let n = 1; n <= this.tier; n++) {
        const v = this.variantForSlot(n);
        if (!v || !v.available) {
          this.showError(
            this.tier > 1
              ? (this.dataset.msgUnavailableSlot || 'Stück #[n]: Diese Auswahl ist leider nicht verfügbar.').replace('[n]', n)
              : this.dataset.msgUnavailable || 'Diese Auswahl ist leider nicht verfügbar.'
          );
          return false;
        }
        counts.set(v.id, (counts.get(v.id) || 0) + 1);
      }
      this.showError('');
      return Array.from(counts, ([id, quantity]) => ({ variantId: String(id), quantity }));
    }
    sync() {
      const tier = this.tier;
      this.querySelectorAll('.hh-qb__opt').forEach((el) =>
        el.classList.toggle('is-selected', Number(el.dataset.tier) === tier)
      );
      const selector = this.section.querySelector('quantity-selector-component');
      if (selector?.setValue) {
        selector.setValue(tier);
      } else {
        const input = this.section.querySelector('input[name="quantity"]');
        if (input) {
          input.value = tier;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  }
  if (!customElements.get('hh-qty-breaks')) customElements.define('hh-qty-breaks', HHQtyBreaks);

  /* ---------- Aktionsleiste mit Countdown ---------- */
  // "2026-11-02T23:59" als deutsche Zeit (Europe/Berlin, Sommer-/Winterzeit) -> Zeitstempel
  const berlinToEpoch = (local) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{1,2}):(\d{2})/.exec(local || '');
    if (!m) return NaN;
    const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]);
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat('en-US', {
        timeZone: 'Europe/Berlin', hourCycle: 'h23',
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
      }).formatToParts(new Date(guess)).map((p) => [p.type, p.value])
    );
    const asBerlin = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute);
    return guess - (asBerlin - guess);
  };

  /** Heutiges Datum in Berlin als "JJJJ-MM-TT" (+ offsetDays) */
  const berlinDate = (offsetDays = 0) => {
    const d = new Date(Date.now() + offsetDays * 86400000);
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin' }).format(d);
  };

  class HHSaleBar extends HTMLElement {
    connectedCallback() {
      this.saleEnd = berlinToEpoch(this.dataset.end); // NaN = kein Sale-Ende
      this.timer = this.querySelector('.hh-sale__timer');
      if (!this.timer) return;
      this.tick();
      this.interval = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() {
      clearInterval(this.interval);
    }
    /** Nächster Countdown-Zielzeitpunkt */
    target(now) {
      if (this.dataset.mode !== 'daily') return this.saleEnd;
      const time = /^\d{1,2}:\d{2}$/.test(this.dataset.daily || '') ? this.dataset.daily : '23:59';
      let t = berlinToEpoch(`${berlinDate()}T${time}`);
      if (t <= now) t = berlinToEpoch(`${berlinDate(1)}T${time}`);
      // Am letzten Sale-Tag nicht über das Sale-Ende hinaus zählen
      return Number.isNaN(this.saleEnd) ? t : Math.min(t, this.saleEnd);
    }
    tick() {
      const now = Date.now();
      if (!Number.isNaN(this.saleEnd) && now >= this.saleEnd) {
        clearInterval(this.interval);
        this.timer.hidden = true;
        if (this.dataset.expired === 'hide') this.hidden = true;
        return;
      }
      const end = this.target(now);
      if (Number.isNaN(end)) return;
      const left = Math.max(0, Math.floor((end - now) / 1000));
      const vals = { d: Math.floor(left / 86400), h: Math.floor(left / 3600) % 24, m: Math.floor(left / 60) % 60, s: left % 60 };
      // Tage nur zeigen, solange mehr als 24 Std. übrig sind
      const showDays = vals.d > 0;
      if (!showDays) vals.h = Math.floor(left / 3600);
      this.timer.querySelector('[data-unit="d"]')?.toggleAttribute('hidden', !showDays);
      this.timer.querySelector('[data-colon="d"]')?.toggleAttribute('hidden', !showDays);
      for (const [unit, val] of Object.entries(vals)) {
        const str = String(val).padStart(2, '0').slice(-2);
        this.timer.querySelectorAll(`[data-unit="${unit}"] b`).forEach((b, i) => {
          if (b.textContent !== str[i]) b.textContent = str[i];
        });
      }
      this.timer.hidden = false;
    }
  }
  if (!customElements.get('hh-sale-bar')) customElements.define('hh-sale-bar', HHSaleBar);

  /* ---------- Variantwissel: prijsrij en (zonder varianten-dropdowns) de box uit verse HTML ---------- */
  const HOOFD = 'product-information, .product-information';
  const refresh = (html) => {
    document.querySelectorAll('[data-hh-refresh]').forEach((el) => {
      const fresh = html.querySelector(`[data-hh-refresh="${el.dataset.hhRefresh}"]`);
      if (!fresh) return;
      if (el.tagName.toLowerCase() === 'hh-qty-breaks') {
        if (el.data) return; // met dropdowns rekent de box zelf
        const remembered = selectedTier[el.dataset.hhRefresh];
        el.innerHTML = fresh.innerHTML;
        if (remembered) {
          const input = el.querySelector(`.hh-qb__head input[value="${remembered}"]`);
          if (input) input.checked = true;
        }
        requestAnimationFrame(() => el.sync());
      } else {
        el.innerHTML = fresh.innerHTML;
      }
    });
  };
  document.addEventListener(
    'shopify:product:select',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('product-card') || target.closest('quick-add-dialog')) return;
      if (!(event.promise instanceof Promise)) return;
      event.promise
        .then((result) => {
          const detail = result?.detail;
          if (!detail?.html) return;
          const here = document.querySelector(`${HOOFD} variant-picker`)?.getAttribute('data-product-id');
          if (here && detail.productId && String(detail.productId) !== String(here)) return;
          refresh(detail.html);
          setTimeout(() => document.querySelectorAll('hh-qty-breaks').forEach((el) => el.sync?.()), 50);
        })
        .catch((err) => { if (err?.name !== 'AbortError') console.warn('[mivoza-offer]', err); });
    },
    true
  );
  document.addEventListener('cart:update', () =>
    setTimeout(() => document.querySelectorAll('hh-qty-breaks').forEach((el) => el.sync?.()), 50)
  );
})();
